import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { store } from '../store.js';
import type { MessageKey } from '../i18n/messages.js';

@customElement('jv-site-header')
export class JvSiteHeader extends LitElement {
  static styles = css`
    :host {
      display: block;
      position: sticky;
      top: 0;
      z-index: 20;
    }
    .topbar {
      background: var(--jv-color-amber);
      color: var(--jv-color-ink);
      font-size: 0.72rem;
      padding: 0.45rem 1.25rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      letter-spacing: 0.04em;
    }
    .topbar .status {
      margin-left: auto;
      text-align: right;
    }
    .bar {
      background: var(--jv-color-bg);
      border-bottom: var(--jv-border-w) solid var(--jv-color-border);
    }
    header {
      max-width: var(--jv-max);
      margin: 0 auto;
      display: grid;
      grid-template-columns: auto 1fr;
      grid-template-areas:
        'brand actions'
        'nav nav';
      gap: 0.75rem 1rem;
      padding: 0.85rem 1.25rem 0.95rem;
      align-items: center;
    }
    .brand {
      grid-area: brand;
      display: flex;
      align-items: center;
      gap: 0.65rem;
      text-decoration: none;
      font-weight: 700;
      font-size: 1.25rem;
    }
    .brand-logo {
      height: 38px;
      width: auto;
      display: block;
      object-fit: contain;
    }
    .prompt {
      color: var(--jv-color-accent);
    }
    .tag {
      color: var(--jv-color-muted);
      font-size: var(--jv-fs-xs);
      font-weight: 400;
      white-space: nowrap;
    }
    nav.main {
      grid-area: nav;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.55rem 1.15rem;
      font-size: 0.78rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding-top: 0.65rem;
      border-top: 1px solid var(--jv-color-border);
    }
    .actions {
      grid-area: actions;
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      align-items: center;
      gap: 0.55rem 0.75rem;
    }
    a {
      text-decoration: none;
      color: var(--jv-color-text);
    }
    a:hover {
      color: var(--jv-color-accent);
    }
    .cart-count {
      color: var(--jv-color-accent);
    }
    .login {
      font-size: 0.78rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .lang {
      display: flex;
      border: var(--jv-border-w) solid var(--jv-color-border);
    }
    .lang button {
      border: 0;
      background: transparent;
      padding: 0.2rem 0.5rem;
      cursor: pointer;
      color: var(--jv-color-muted);
      font-family: inherit;
      font-size: 0.72rem;
    }
    .lang button[aria-pressed='true'] {
      background: var(--jv-color-accent);
      color: var(--jv-color-on-accent);
    }
    .topbar .lang {
      border-color: var(--jv-color-ink);
    }
    .topbar .lang button {
      color: var(--jv-color-ink);
    }
    @media (min-width: 1080px) {
      header {
        grid-template-columns: auto 1fr auto;
        grid-template-areas: 'brand nav actions';
      }
      nav.main {
        justify-content: center;
        padding-top: 0;
        border-top: 0;
      }
    }
    @media (max-width: 720px) {
      .tag {
        display: none;
      }
      .topbar .status {
        display: none;
      }
      header {
        padding: 0.6rem 0.85rem;
        gap: 0.5rem 0.75rem;
      }
      nav.main {
        gap: 0.4rem 0.85rem;
        font-size: 0.74rem;
        overflow-x: auto;
        padding-bottom: 0.25rem;
      }
      .actions {
        gap: 0.4rem 0.5rem;
      }
      .actions jv-button {
        --jv-btn-padding: 0.4rem 0.75rem;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;

  private t(key: MessageKey) {
    return store.t(key);
  }

  render() {
    const cart = store.cartCount;
    return html`
      <div class="topbar">
        <div class="lang">
          <button
            aria-pressed=${store.locale === 'fr'}
            @click=${() => store.setLocale('fr')}
          >
            ${this.t('lang.fr')}
          </button>
          <button
            aria-pressed=${store.locale === 'en'}
            @click=${() => store.setLocale('en')}
          >
            ${this.t('lang.en')}
          </button>
        </div>
        <span class="status">${this.t('sys.status')}</span>
      </div>
      <div class="bar">
        <header>
          <a class="brand" href="/" aria-label="Juvo Home">
            <img class="brand-logo" src="/assets/logo_full.png" alt="juvo" />
            <span class="tag">${this.t('brand.tag')}</span>
          </a>
          <nav class="main">
            <a href="/about">${this.t('nav.about')}</a>
            <a href="/courses">${this.t('nav.courses')}</a>
            <a href="/donate">${this.t('nav.donate')}</a>
            <a href="/merch">${this.t('nav.merch')}</a>
            <a href="/impact">${this.t('nav.impact')}</a>
            <a href="/geeks">${this.t('nav.geeks')}</a>
          </nav>
          <div class="actions">
            <a href="/merch/cart">
              ${this.t('nav.cart')}${cart ? html`<span class="cart-count">(${cart})</span>` : ''}
            </a>
            ${store.user
              ? html`<a class="login" href="/portal">${this.t('nav.portal')}</a>
                  ${store.isStaff ? html`<a class="login" href="/admin">${this.t('nav.admin')}</a>` : ''}
                  <a class="login" href="/" @click=${this.onLogout}>${this.t('nav.logout')}</a>`
              : html`<a class="login" href="/login">${this.t('nav.login')}</a>
                  <jv-button href="/signup" variant="primary">${this.t('nav.signup')}</jv-button>`}
          </div>
        </header>
      </div>
    `;
  }

  private onLogout = async (event: Event) => {
    event.preventDefault();
    await store.logout();
    history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
}

@customElement('jv-site-footer')
export class JvSiteFooter extends LitElement {
  static styles = css`
    :host {
      display: block;
      margin-top: auto;
      background: var(--jv-color-ink);
      color: #c9cdc9;
    }
    footer {
      max-width: var(--jv-max);
      margin: 0 auto;
      padding: 3rem 1.25rem 1.5rem;
    }
    .grid {
      display: grid;
      gap: 1.5rem;
      font-size: var(--jv-fs-sm);
    }
    @media (min-width: 800px) {
      .grid {
        grid-template-columns: 1.4fr 1fr 1fr 1fr;
      }
    }
    h5 {
      color: #fff;
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin: 0 0 0.85rem;
    }
    p,
    a {
      color: #9ba39c;
      text-decoration: none;
      display: block;
      margin: 0 0 0.5rem;
    }
    a:hover {
      color: var(--jv-color-amber);
    }
    .bottom {
      text-align: center;
      font-size: 0.7rem;
      color: #6e756f;
      margin-top: 2rem;
      padding-top: 1.2rem;
      border-top: 1px solid #2a2e2a;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  render() {
    return html`
      <footer>
        <div class="grid">
          <div>
            <div style="margin-bottom: 0.75rem;">
              <img src="/assets/logo_full_light.png" alt="juvo" style="height: 34px; width: auto; display: block;" />
            </div>
            <p>${store.t('footer.mission')}</p>
          </div>
          <div>
            <h5>${store.t('footer.platform')}</h5>
            <a href="/about">${store.t('nav.about')}</a>
            <a href="/courses">${store.t('nav.courses')}</a>
            <a href="/donate">${store.t('nav.donate')}</a>
            <a href="/merch">${store.t('nav.merch')}</a>
            <a href="/impact">${store.t('nav.impact')}</a>
          </div>
          <div>
            <h5>${store.t('footer.contact')}</h5>
            <a href="mailto:contact@juvo.healthgeek.org.ml">${store.t('footer.email')}</a>
            <p>${store.t('footer.location')}</p>
          </div>
          <div>
            <h5>${store.t('footer.newsletter')}</h5>
            <p>${store.t('footer.newsletterBody')}</p>
          </div>
        </div>
        <p class="bottom">${store.t('footer.rights')}</p>
      </footer>
    `;
  }
}

@customElement('jv-page')
export class JvPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
      max-width: var(--jv-max);
      margin: 0 auto;
      padding: 1.5rem 1rem 3rem;
      box-sizing: border-box;
    }
    @media (min-width: 640px) {
      :host {
        padding: 2.25rem 1.5rem 4rem;
      }
    }
  `;
  render() {
    return html`<slot></slot>`;
  }
}
